import HBPill from '@honeybook/hbui/dist/components/HBPill/HBPill';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { PillType } from '@honeybook/hbui/dist/components/HBPill/HBPill.types';
import MemberAvatarList from '~/components/Builder/BuilderSidebar/ScheduleSidebar/SessionConfig/MemberAvatarList';
import { ISessionConfig } from '~/types/server.types';
import { normalizeSessionConfig } from '~/common/Schedule/schedule.utils';
import { Trans } from 'react-i18next';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBClickable from '@honeybook/hbui/dist/components/HBClickable/HBClickable';
import { SessionConfigInfoText } from '~/components/common/SessionConfigItem/SessionConfigItem.styles';
import SessionConfigItemOwnerName from '~/components/common/SessionConfigItem/SessionConfigItemOwnerName';
import Connected16 from '@honeybook/icons/dist/icons/Connected16';
import colors from '@honeybook/hbui/dist/styles/colors';
import is from 'styled-is';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';

interface Props {
    sessionConfig: ISessionConfig;
    className?: string;
    children?: ReactNode | ReactNode[];
    onClick?: () => void;
    isWithSessionWindow?: boolean;
    isWithTimezone?: boolean;
    isWithTeamMembers?: boolean;
    isWithOwnerName?: boolean;
    isEmphasized?: boolean;
    isLinkedToService?: boolean;
    testId?: string;
}

export const SessionConfigItem = ({
    sessionConfig,
    className,
    children,
    onClick,
    isWithSessionWindow = true,
    isWithTimezone = true,
    isWithTeamMembers = true,
    isWithOwnerName = false,
    isEmphasized = true,
    isLinkedToService = false,
    testId
}: Props) => {
    const { session_name, shared_members, enabled } = sessionConfig;

    const {
        userStore: { currUser }
    } = useGlobalMX();

    const {
        sessionColor,
        normalizedSessionTypeAndDuration,
        normalizedSessionWindowAndTimezone
    } = normalizeSessionConfig({ sessionConfig, isWithTimezone });

    // only show if session has members that are not the current user
    const showTeamMembers = shared_members?.some(
        member => member._id !== currUser._id
    );

    return (
        <SessionConfigContainer
            className={className}
            onClick={onClick}
            testIdName={testId ? '' : 'session-config'}
            testId={testId}
        >
            <SessionConfigTypeColoredBox backgroundColor={sessionColor} />
            <SessionConfigDetailsSection>
                <SessionNameContainer $isLinkedToService={isLinkedToService}>
                    <SessionNameText type={isEmphasized ? 'title16' : 'body14'}>
                        {session_name}
                        {'  '}
                        {!enabled && (
                            <HBTooltip
                                placement="top"
                                target={
                                    <InactivePill
                                        theme={PillType.Template}
                                        label="inactive"
                                        testIdName="session-config-inactive"
                                    />
                                }
                                disabled={sessionConfig.enabled}
                                targetContainerStyle={{ display: 'inline' }}
                                maxWidth="237px"
                            >
                                {/* @ts-ignore */}
                                <Trans
                                    i18nKey={
                                        'schedule.session_config_sidebar.disabled_session_tooltip'
                                    }
                                >
                                    <strong />
                                </Trans>
                            </HBTooltip>
                        )}
                    </SessionNameText>
                    {isLinkedToService && <Connected16 color={colors.blue20} />}
                </SessionNameContainer>
                <HBText
                    type={isEmphasized ? 'body14' : 'body12'}
                    color="textSecondary"
                >
                    {normalizedSessionTypeAndDuration}
                </HBText>
                {isWithOwnerName && (
                    <SessionConfigItemOwnerName sessionConfig={sessionConfig} />
                )}
                {isWithSessionWindow && (
                    <SessionConfigInfoText>
                        {normalizedSessionWindowAndTimezone}
                    </SessionConfigInfoText>
                )}
                {isWithTeamMembers && showTeamMembers && (
                    <MemberAvatarList sharedMembers={shared_members} />
                )}
                {children}
            </SessionConfigDetailsSection>
        </SessionConfigContainer>
    );
};

// -------------------------------- Styles -------------------------------- //

const SessionConfigContainer = styled(HBClickable)`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    align-self: stretch;
    width: auto;
    gap: 10px;
`;

const SessionConfigTypeColoredBox = styled.div<{ backgroundColor: string }>`
    background: ${props => props.backgroundColor};
    flex: 0 0 12px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 3px;
`;

const SessionConfigDetailsSection = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 2px;
`;

export const SessionNameText = styled(HBText)`
    word-break: break-word;
    margin: 0 8px 3px 0;
    white-space: pre-wrap;
`;

const InactivePill = styled(HBPill)`
    display: inline;
    vertical-align: middle;
`;

const SessionNameContainer = styled.div<{ $isLinkedToService: boolean }>`
    ${is('$isLinkedToService')`
        display: flex;
        justify-content: space-between;
    `}
`;
